import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { apiPost, ApiError } from '../lib/api';
import { Alert, Button, Field, Input, Loading, Spinner, Wordmark } from '../components/ui';
import {
  CHANNELS,
  COMPANY_SIZES,
  CONSUMER_SIGNAL_GROUPS,
  GEO_OPTIONS,
  GeoScope,
  PROSPECT_SIGNALS,
  SELL_TO_OPTIONS,
  SellTo,
  walkinRecommended,
} from '../lib/onboardingConfig';

interface Draft {
  name: string;
  website: string;
  industry: string;
  companySize: string;
  isGroup: boolean;
  sellTo: SellTo | null;
  geoScope: GeoScope | null;
  geoCenter: string;
  geoRadiusMi: string;
  targetCompanySizes: string[];
  targetIndustries: string;
  targetTitles: string;
  typicalDealSize: string;
  typicalSalesCycle: string;
  idealCustomerDesc: string;
  signals: string[];
  signalNotes: string;
  channels: string[];
  brandTone: string;
  brandNotes: string;
}

const emptyDraft = (name: string): Draft => ({
  name,
  website: '',
  industry: '',
  companySize: '',
  isGroup: false,
  sellTo: null,
  geoScope: null,
  geoCenter: '',
  geoRadiusMi: '25',
  targetCompanySizes: [],
  targetIndustries: '',
  targetTitles: '',
  typicalDealSize: '',
  typicalSalesCycle: '',
  idealCustomerDesc: '',
  signals: [],
  signalNotes: '',
  channels: ['email', 'calls'],
  brandTone: 'Direct and practical',
  brandNotes: '',
});

function toProfilePayload(d: Draft) {
  return {
    account: {
      name: d.name,
      website: d.website,
      industry: d.industry,
      companySize: d.companySize,
      isGroup: d.isGroup,
      channels: d.channels,
      brandVoice: { tone: d.brandTone, notes: d.brandNotes },
    },
    profile: {
      sellTo: d.sellTo,
      geoScope: d.geoScope,
      geoCenter: d.geoCenter,
      geoRadiusMi: d.geoRadiusMi ? Number(d.geoRadiusMi) : null,
      targetCompanySizes: d.targetCompanySizes,
      targetIndustries: splitList(d.targetIndustries),
      targetTitles: splitList(d.targetTitles),
      typicalDealSize: d.typicalDealSize,
      typicalSalesCycle: d.typicalSalesCycle,
      idealCustomerDesc: d.idealCustomerDesc,
      signals: d.signals,
      signalNotes: d.signalNotes,
    },
  };
}
const splitList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

export default function Onboarding() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState<Draft>(() => emptyDraft(me?.group?.name || ''));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourcing, setSourcing] = useState<{ mode: string; note: string } | null>(null);

  const set = (patch: Partial<Draft>) => setD((prev) => ({ ...prev, ...patch }));
  const isB2Bish = d.sellTo === 'b2b' || d.sellTo === 'local_business';

  // The step list is dynamic: the team-setup step only exists for groups.
  const steps = useMemo(() => {
    const base = ['welcome', 'business', 'sellto', 'geo', 'prospect', 'sourcing', 'channels', 'brand'];
    if (d.isGroup) base.push('team');
    base.push('preview', 'done');
    return base;
  }, [d.isGroup]);

  const key = steps[step];
  const pct = Math.round((step / (steps.length - 1)) * 100);

  async function autosave(completed = false) {
    const payload = toProfilePayload(d);
    const res = await apiPost<{ sourcing: { mode: string; note: string } }>('/api/onboarding', {
      ...payload,
      completed,
    });
    return res;
  }

  async function next() {
    setError(null);
    // Per-step validation.
    if (key === 'business' && !d.name.trim()) return setError('What should we call your workspace?');
    if (key === 'sellto' && !d.sellTo) return setError('Pick who you sell to so we can shape the rest.');
    if (key === 'geo' && !d.geoScope) return setError('Where are your buyers?');

    setBusy(true);
    try {
      // On leaving the "prospect" step, save + fetch the authoritative sourcing decision.
      if (key === 'prospect') {
        const res = await autosave(false);
        setSourcing(res.sourcing);
      }
      setStep((s) => Math.min(s + 1, steps.length - 1));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  }
  const back = () => { setError(null); setStep((s) => Math.max(0, s - 1)); };

  async function finish() {
    setBusy(true);
    setError(null);
    try {
      await autosave(true);
      await refresh();
      navigate('/contacts', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not finish onboarding.');
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col px-5 py-8">
      <div className="mb-6 flex items-center justify-between">
        <Wordmark />
        <span className="text-xs text-muted">Setup · {pct}%</span>
      </div>
      <div className="mb-8 h-1.5 overflow-hidden rounded-full bg-surface-3">
        <div className="h-full rounded-full bg-gradient-to-r from-power to-electric transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="flex-1">
        {key === 'welcome' && <Welcome name={me?.user.fullName} steps={steps.length - 2} />}
        {key === 'business' && <Business d={d} set={set} />}
        {key === 'sellto' && <SellToStep d={d} set={set} isB2Bish={isB2Bish} />}
        {key === 'geo' && <Geo d={d} set={set} />}
        {key === 'prospect' && <Prospect d={d} set={set} />}
        {key === 'sourcing' && <Sourcing sourcing={sourcing} />}
        {key === 'channels' && <Channels d={d} set={set} />}
        {key === 'brand' && <Brand d={d} set={set} />}
        {key === 'team' && <Team />}
        {key === 'preview' && <Preview d={d} />}
        {key === 'done' && <Done isGroup={d.isGroup} />}
      </div>

      {error && <div className="my-4"><Alert>{error}</Alert></div>}

      <div className="mt-8 flex items-center justify-between">
        <button onClick={back} disabled={step === 0 || busy} className="text-sm text-muted hover:text-soft disabled:opacity-40">
          ← Back
        </button>
        {key === 'done' ? (
          <Button onClick={finish} loading={busy}>Go to my dashboard</Button>
        ) : (
          <Button onClick={next} loading={busy}>{key === 'welcome' ? "Let's go" : 'Continue'}</Button>
        )}
      </div>
    </div>
  );
}

/* ---------- step views ---------- */

function StepHead({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-6">
      <h1 className="h-display text-3xl text-soft">{title}</h1>
      {sub && <p className="mt-2 text-muted">{sub}</p>}
    </div>
  );
}

function Choice({
  active, title, hint, onClick, disabled,
}: { active: boolean; title: string; hint?: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full rounded-xl border px-4 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-40 ${
        active ? 'border-power bg-power/10 ring-2 ring-power/30' : 'border-line bg-surface-2 hover:border-line-strong'
      }`}
    >
      <div className="font-head font-semibold text-soft">{title}</div>
      {hint && <div className="mt-0.5 text-sm text-muted">{hint}</div>}
    </button>
  );
}

function Welcome({ name, steps }: { name?: string | null; steps: number }) {
  return (
    <div>
      <StepHead
        title={`Let's set up your targeting, ${name?.split(' ')[0] || 'welcome'}`}
        sub={`About ${steps} quick steps. We're not just creating your account — we're building the profile that teaches your tools how to find your prospects. Be honest and specific; it pays off immediately.`}
      />
      <div className="card px-4 py-4 text-sm text-muted">
        You can change any of this later in Settings. Nothing here is permanent.
      </div>
    </div>
  );
}

function Business({ d, set }: { d: Draft; set: (p: Partial<Draft>) => void }) {
  return (
    <div>
      <StepHead title="About your business" sub="The basics, so the tools can speak in your voice." />
      <div className="space-y-4">
        <Field label="Workspace name"><Input value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder="Bode Equipment" /></Field>
        <Field label="Website" hint="We'll use this to auto-fill details where we can."><Input value={d.website} onChange={(e) => set({ website: e.target.value })} placeholder="https://…" /></Field>
        <Field label="Your industry"><Input value={d.industry} onChange={(e) => set({ industry: e.target.value })} placeholder="Industrial equipment" /></Field>
        <Field label="Company size">
          <div className="flex flex-wrap gap-2">
            {COMPANY_SIZES.map((s) => (
              <button key={s} type="button" onClick={() => set({ companySize: s })}
                className={`rounded-lg border px-3 py-1.5 text-sm ${d.companySize === s ? 'border-power bg-power/10 text-soft' : 'border-line text-muted hover:text-soft'}`}>{s}</button>
            ))}
          </div>
        </Field>
        <Field label="Are you setting this up just for yourself, or for a group?">
          <div className="grid gap-2 sm:grid-cols-2">
            <Choice active={!d.isGroup} title="Just me" hint="A workspace of one." onClick={() => set({ isGroup: false })} />
            <Choice active={d.isGroup} title="For a team" hint="Invite reps, share pipeline." onClick={() => set({ isGroup: true })} />
          </div>
        </Field>
      </div>
    </div>
  );
}

function SellToStep({ d, set, isB2Bish }: { d: Draft; set: (p: Partial<Draft>) => void; isB2Bish: boolean }) {
  return (
    <div>
      <StepHead title="Who do you sell to?" sub="This shapes everything else — pick the closest fit." />
      <div className="space-y-2">
        {SELL_TO_OPTIONS.map((o) => (
          <Choice key={o.value} active={d.sellTo === o.value} title={o.label} hint={o.hint} onClick={() => set({ sellTo: o.value })} />
        ))}
      </div>

      {isB2Bish && (
        <div className="mt-6 space-y-4 rounded-xl border border-line bg-surface/40 p-4">
          <p className="text-sm font-semibold text-electric">A bit more about your ideal accounts</p>
          <Field label="Target industries" hint="Comma-separated."><Input value={d.targetIndustries} onChange={(e) => set({ targetIndustries: e.target.value })} placeholder="Manufacturing, biopharma, aerospace" /></Field>
          {d.sellTo === 'b2b' && (
            <>
              <Field label="Target buyer titles" hint="Who signs off? Comma-separated."><Input value={d.targetTitles} onChange={(e) => set({ targetTitles: e.target.value })} placeholder="Facilities Director, VP Operations" /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Typical deal size"><Input value={d.typicalDealSize} onChange={(e) => set({ typicalDealSize: e.target.value })} placeholder="$25k" /></Field>
                <Field label="Typical sales cycle"><Input value={d.typicalSalesCycle} onChange={(e) => set({ typicalSalesCycle: e.target.value })} placeholder="3–6 months" /></Field>
              </div>
            </>
          )}
        </div>
      )}

      {d.sellTo === 'consumers' && (
        <div className="mt-6 rounded-xl border border-line bg-surface/40 p-4 text-sm text-muted">
          You sell to people, not businesses — we'll ask what makes a good prospect next, with
          consumer signals (not company data). <span className="text-soft/90">Heads up:</span> automatic
          consumer sourcing isn't live yet, so these signals shape scoring and your manual/imported
          lists today rather than auto-finding people for you. Import a list or add contacts and every
          tool works right away.
        </div>
      )}

      {/* Multi-segment signpost (QA P2: a seller with several buyer types shouldn't
          feel forced into one). */}
      <p className="mt-5 text-xs text-muted">
        Sell to more than one type of buyer? Set up your main one now — add the others any time in
        <span className="text-soft"> Settings → Targeting</span>.
      </p>
    </div>
  );
}

function Geo({ d, set }: { d: Draft; set: (p: Partial<Draft>) => void }) {
  const local = d.geoScope === 'hyperlocal' || d.geoScope === 'regional';
  return (
    <div>
      <StepHead title="Where are your buyers?" sub="So we know how wide to cast the net." />
      <div className="space-y-2">
        {GEO_OPTIONS.map((o) => (
          <Choice key={o.value} active={d.geoScope === o.value} title={o.label} hint={o.hint} onClick={() => set({ geoScope: o.value })} />
        ))}
      </div>
      {local && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field label="City or area"><Input value={d.geoCenter} onChange={(e) => set({ geoCenter: e.target.value })} placeholder="Manchester, NH" /></Field>
          <Field label="Radius (miles)"><Input type="number" min={1} max={500} value={d.geoRadiusMi} onChange={(e) => set({ geoRadiusMi: e.target.value.replace(/[^0-9]/g, '') })} /></Field>
        </div>
      )}
    </div>
  );
}

function Prospect({ d, set }: { d: Draft; set: (p: Partial<Draft>) => void }) {
  const signals = PROSPECT_SIGNALS.filter((s) => !d.sellTo || s.appliesTo.includes(d.sellTo));
  const toggle = (k: string) => set({ signals: d.signals.includes(k) ? d.signals.filter((x) => x !== k) : [...d.signals, k] });
  // If any chosen signal is "detailable", ask for the specifics (which title? which software?).
  const needsDetail = signals.some((s) => s.detailable && d.signals.includes(s.key));
  return (
    <div>
      <StepHead title="What makes someone a good prospect?" sub="This is where we decide whether we can find them automatically." />
      <Field label="Describe your ideal customer">
        <textarea className="input min-h-[90px]" value={d.idealCustomerDesc} onChange={(e) => set({ idealCustomerDesc: e.target.value })}
          placeholder="The more specific, the better we can target." />
      </Field>
      <p className="mb-2 mt-5 font-head text-sm font-semibold text-soft/90">Buying signals that matter (pick any)</p>
      {d.sellTo === 'consumers' ? (
        // Split consumer signals by seller type so a home-services account isn't shown
        // real-estate-investor signals (pre-foreclosure/probate) in one flat list.
        <div className="space-y-4">
          {CONSUMER_SIGNAL_GROUPS.map((g) => {
            const groupSignals = signals.filter((s) => s.group === g.group);
            if (!groupSignals.length) return null;
            return (
              <div key={g.group}>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">{g.label}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {groupSignals.map((s) => (
                    <button key={s.key} type="button" onClick={() => toggle(s.key)}
                      className={`rounded-xl border px-3 py-2.5 text-left text-sm ${d.signals.includes(s.key) ? 'border-power bg-power/10 text-soft' : 'border-line text-muted hover:text-soft'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {signals.map((s) => (
            <button key={s.key} type="button" onClick={() => toggle(s.key)}
              className={`rounded-xl border px-3 py-2.5 text-left text-sm ${d.signals.includes(s.key) ? 'border-power bg-power/10 text-soft' : 'border-line text-muted hover:text-soft'}`}>
              {s.label}
            </button>
          ))}
        </div>
      )}
      {needsDetail && (
        <div className="mt-4">
          <Field label="Name the specifics" hint="Which titles, software, or certifications? e.g. “Integrator”, “Salesforce”, “30+ yr old home”.">
            <Input value={d.signalNotes} onChange={(e) => set({ signalNotes: e.target.value })} placeholder="Integrator, Visionary, EOS Implementer" />
          </Field>
        </div>
      )}
    </div>
  );
}

function Sourcing({ sourcing }: { sourcing: { mode: string; note: string } | null }) {
  if (!sourcing) return <Loading label="Working out how we'll find your prospects…" />;
  const badge: Record<string, { label: string; cls: string }> = {
    places: { label: 'We can auto-source these', cls: 'border-success/40 bg-success/10 text-success' },
    signal: { label: 'Best added by name — not a map search', cls: 'border-victory/40 bg-victory/10 text-victory' },
    manual: { label: 'Manual for now', cls: 'border-electric/40 bg-electric/10 text-electric' },
    unknown: { label: 'Need a bit more', cls: 'border-line-strong bg-surface-3 text-muted' },
  };
  const b = badge[sourcing.mode] || badge.unknown;
  return (
    <div>
      <StepHead title="Here's how we'll find your prospects" sub="Straight answer — no vague promises." />
      <div className="card px-5 py-5">
        <span className={`inline-block rounded-md border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${b.cls}`}>{b.label}</span>
        <p className="mt-3 leading-relaxed text-soft/90">{sourcing.note}</p>
      </div>
    </div>
  );
}

function Channels({ d, set }: { d: Draft; set: (p: Partial<Draft>) => void }) {
  // QA P2/P4: don't hard-disable walk-in on geo — a regional/national field team
  // still does visits. Keep it selectable; just note when most sellers skip it.
  const walkinFit = walkinRecommended(d.sellTo, d.geoScope);
  const toggle = (k: string) => set({ channels: d.channels.includes(k) ? d.channels.filter((x) => x !== k) : [...d.channels, k] });
  return (
    <div>
      <StepHead title="How do you want to reach them?" sub="Turn on what fits. You can change this any time." />
      <div className="space-y-2">
        {CHANNELS.map((c) => {
          const softNote = c.key === 'walkin' && !walkinFit;
          return (
            <Choice key={c.key} active={d.channels.includes(c.key)}
              title={c.label}
              hint={softNote ? 'Most remote/national sellers skip this — but leave it on if you do field visits.' : c.hint}
              onClick={() => toggle(c.key)} />
          );
        })}
      </div>
    </div>
  );
}

function Brand({ d, set }: { d: Draft; set: (p: Partial<Draft>) => void }) {
  return (
    <div>
      <StepHead title="Your voice" sub="Quick pass — refine it later in Settings. This shapes your emails and content." />
      <Field label="Tone"><Input value={d.brandTone} onChange={(e) => set({ brandTone: e.target.value })} placeholder="Direct and practical" /></Field>
      <div className="mt-4">
        <Field label="Anything we should know?" hint="Phrases you love, words you'd never use.">
          <textarea className="input min-h-[90px]" value={d.brandNotes} onChange={(e) => set({ brandNotes: e.target.value })} />
        </Field>
      </div>
    </div>
  );
}

function Team() {
  return (
    <div>
      <StepHead title="Add your team" sub="Invite reps now, or skip and do it later from the Team page." />
      <div className="card px-4 py-4 text-sm text-muted">
        You'll finish setup first, then invite teammates from <span className="text-soft">Team</span> — each gets their own login,
        and you choose whether they see the whole pipeline (manager) or just their own book (member).
      </div>
    </div>
  );
}

function Preview({ d }: { d: Draft }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [res, setRes] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setState('loading');
    setErr(null);
    try {
      const r = await apiPost('/api/onboarding/preview', { profile: toProfilePayload(d).profile });
      setRes(r);
      setState('done');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Preview failed.');
      setState('idle');
    }
  }

  return (
    <div>
      <StepHead title="A quick look at what we'd find" sub="Sanity-check your targeting before you commit." />
      {state === 'idle' && <Button onClick={run}>Show me sample prospects</Button>}
      {state === 'loading' && <div className="flex items-center gap-2 text-muted"><Spinner className="h-4 w-4" /> Searching…</div>}
      {err && <div className="mt-4"><Alert>{err}</Alert></div>}
      {state === 'done' && res && (
        <div className="mt-4 space-y-3">
          <div className="card px-4 py-3 text-sm text-soft/90">{res.sourcing?.note}</div>
          {res.live && res.prospects?.length > 0 ? (
            res.prospects.map((p: any, i: number) => (
              <div key={i} className="card flex items-center justify-between px-4 py-3">
                <div><div className="font-head font-semibold text-soft">{p.businessName}</div><div className="text-xs text-muted">{p.address}</div></div>
                {p.phone && <span className="text-xs text-muted">{p.phone}</span>}
              </div>
            ))
          ) : (
            <div className="card px-4 py-4 text-sm text-muted">{res.manualPath || res.reason}</div>
          )}
        </div>
      )}
    </div>
  );
}

function Done({ isGroup }: { isGroup: boolean }) {
  return (
    <div>
      <StepHead title="You're set." sub="Your targeting profile is saved and your tools know who to look for." />
      <ul className="space-y-2 text-soft/90">
        <li className="card px-4 py-3">✓ Targeting profile built</li>
        <li className="card px-4 py-3">✓ Outreach channels configured</li>
        <li className="card px-4 py-3">{isGroup ? '→ Next: invite your team from the Team page' : '→ Next: find your first prospects'}</li>
      </ul>
    </div>
  );
}
